/* eslint-disable no-nested-ternary */
import {
    findMinAndMax,
    newAdaptProjectionData,
} from 'adaptors/adaptProjection';
import ProjectionTooltip from 'components/ProjectionTooltip';
import { THREEBTYPES } from 'constants/constants';
import { projectionOptional } from 'constants/goalCreation';
import {
    useCreatePortfolioActions,
    useCreatePortfolioSelectors,
    useCreatePortfolioState,
} from 'datasource/CreatePortfolio';
import HandlerError from 'errors/HandlerError';
import { usePortfolioProcessNavigation } from 'hooks/helpers/usePortfolioProcessNavigation';
import { useThreeBProductType } from 'hooks/isThreeBProduct';
import { useFormatting } from 'locale';
import { riskCategoriesByLang } from 'pages/ClientOverview/pages/Portfolios/constants';
import {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import ReactDOMServer from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { formatNumberRounding } from 'utils/formatting';
import handlerRequestCanceling from 'utils/handlerRequestCanceling';
import {
    IsAllDataNull,
    validateInputNumberWithZero,
    validateInputString,
} from 'validation/validation';
import { calculatePlannedInvestment } from 'pages/utils';
import { MAX_INVESTMENT_WITHOUT_PROOF_OF_ASSETS } from 'datasource/CreatePortfolio/constants';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { errorModel } from '../models/errorModel';
import getInvestmentProjection from '../services/getInvestmentProjection';
import { useInvestmentContributions } from './useInvestmentContributions';

let transferValueTimeoutId;
let yearlyPaymentTimeoutId;
let projectionYearsTimeoutId;

const INPUT_TIMEOUT = 200;
const MAX_INVESTMENT_FOR_NEXT_6_MONTHS = 200000;
const PROJECTION_YEARS_PER_INVESTMENT_HORIZON = {
    0: 3,
    1: 5,
    2: 7,
    3: 10,
    4: 20,
};

export const useGoalAndPerformance = ({
    continueDisabledFromProps = false,
}) => {
    const {
        currentInvestmentApplicationSelector,
        is3aProductGroupSelector: is3aProduct,
        is3bProductGroupSelector: isThreeB,
        isVbVbPremiumProductGroupSelector: isVbVbPremiumProduct,
        is3aPremiumProductGroupSelector,
        selectedProductSelector,
        currentInvestmentDescriptionSelector,
        currentClientSelector,
        selectedProductDescSelector,
        isZivZifd,
        totalPillar3aAmountSelector,
    } = useCreatePortfolioSelectors();

    const { saveInvestmentApplicationDataAction } = useCreatePortfolioActions();

    const { shouldInitiateAdvisoryProcess, currentInvestmentApplicationId } = useCreatePortfolioState();

    const modelPortfolioId = currentInvestmentDescriptionSelector.selectStrategy?.modelPortfolioId;
    const isZurichEmployee = currentInvestmentDescriptionSelector?.personalDetails?.zurichEmployee;

    const {
        i18n: { language },
        t,
    } = useTranslation();
    const [rubrik, setRubrik] = useState();
    const [transferValue, setTransferValue] = useState<number>(0);
    const [yearlyPayment, setYearlyPayment] = useState(0);
    const [minMaxTransferValue, setMinMaxTransferValue] = useState(undefined);
    const [minMaxYearlyPayment, setMinMaxYearlyPayment] = useState({
        min: 0,
        max: 6683,
    });
    const [isFullTransfer, setFullTransfer] = useState('true');
    const [chartData, setChartData] = useState([]);
    const [returnRates, setReturnRates] = useState<any>(null);
    const [isLoading, setLoading] = useState(false);
    const [prevPensionScheme, setPrevPensionScheme] = useState<any>({});
    const [validationPrevPensionScheme, setValidationPrevPensionScheme] = useState({});
    const [plannedInvestment, setPlannedInvestment] = useState(0);
    const { getFormattedNumber, getFormattedCurrency } = useFormatting();
    const [pageErrors, setPageErrors] = useState(undefined);
    const [projectionYears, setProjectionYears] = useState<number | undefined>(undefined);
    const [paymentPeriod, setPaymentPeriod] = useState<number>();
    const [periodicPayment, setPeriodicPayment] = useState<number>();
    const [oneTimeInvestment, setOneTimeInvestment] = useState<number>(0);
    const [isPaymentPeriodVisible, setPaymentPeriodVisibility] = useState(false);
    const [isContinueInProgress, setIsContinueInProgress] = useState(false);

    const riskCategoriesList = useMemo(() => riskCategoriesByLang(t), [t]);
    const productName = useMemo(
        () => selectedProductSelector?.name,
        [selectedProductSelector?.name],
    );
    const productSettings = useMemo(
        () => selectedProductSelector?.settings,
        [selectedProductSelector?.settings],
    );
    const affiliatedToPensionFund = useMemo(
        () => currentInvestmentDescriptionSelector?.personalDetails
            ?.affiliatedToPensionFund,
        [
            currentInvestmentDescriptionSelector?.personalDetails
                ?.affiliatedToPensionFund,
        ],
    );
    const individualPortfolio = useMemo(
        () => currentInvestmentDescriptionSelector?.selectStrategy
            ?.individualPortfolio,
        [
            currentInvestmentDescriptionSelector?.selectStrategy
                ?.individualPortfolio,
        ],
    );
    const showFullTransferQuestion = useMemo(
        () => !productSettings?.showYearlyPayments,
        [productSettings?.showTransferQuestion],
    );
    const initialInvestmentAmount = useMemo(
        () => +(
            currentInvestmentApplicationSelector.goals
                ?.InitialInvestmentAmount || 0
        ),
        [currentInvestmentApplicationSelector.goals?.InitialInvestmentAmount],
    );
    const isWithdrawal = useMemo(
        () => selectedProductSelector?.selectStrategy?.withdrawalOption,
        [selectedProductSelector?.selectStrategy?.withdrawalOption],
    );
    const { isAzp, isZic, isZifd } = useThreeBProductType({
        product: selectedProductSelector,
    });

    const withdrawalAzpInvestmentAmount = useMemo(
        () => currentInvestmentDescriptionSelector?.withdrawal?.investmentAmount,
        [currentInvestmentDescriptionSelector?.withdrawal?.investmentAmount],
    );

    const minPeriodicPaymentFor3B = useMemo(
        () => (isZurichEmployee
            ? productSettings?.minPeriodicPaymentZurich
                  ?? productSettings?.minPeriodicPayment
            : productSettings?.minPeriodicPayment),
        [
            isZurichEmployee,
            productSettings?.minPeriodicPaymentZurich,
            productSettings?.minPeriodicPayment,
        ],
    );
    const minOneTimeInvestmentFor3B = useMemo(
        () => (isZurichEmployee
            ? productSettings?.minOneTimeInvestmentZurich
                  ?? productSettings?.minOneTimeInvestment
            : productSettings?.minOneTimeInvestment),
        [
            isZurichEmployee,
            productSettings?.minOneTimeInvestmentZurich,
            productSettings?.minOneTimeInvestment,
        ],
    );

    const {
        data: modelPortfolioData,
        isLoading: isModelPortfolioLoading,
    } = useGetCustomService({
        service: 'portfolioManagement',
        api: 'getModelPortfolio',
        payload: [modelPortfolioId, { language }],
        enabled: !!modelPortfolioId && !!language,
    });

    const modelPortfolio = useMemo(() => modelPortfolioData?.data, [modelPortfolioData?.data]);
    const currency = useMemo(() => modelPortfolioData?.data?.BaseCurrency?.CurrencyCode, [modelPortfolioData?.data?.BaseCurrency?.CurrencyCode]);

    const { getYearlyContribution, getInvestmentForNextSixMonths } = useInvestmentContributions(
        selectedProductSelector?.externalId,
        productSettings,
        {
            periodicPayment,
            paymentPeriod,
            oneTimeInvestment,
        },
    );

    const onPeriodicPaymentChange = useCallback(
        (value) => {
            clearTimeout(yearlyPaymentTimeoutId);

            setPaymentPeriodVisibility(value !== 0);
            yearlyPaymentTimeoutId = setTimeout(() => {
                setPeriodicPayment(value);
                setPlannedInvestment(
                    calculatePlannedInvestment(
                        oneTimeInvestment,
                        value,
                        paymentPeriod as number,
                    ),
                );
            }, 500);
        },
        [
            oneTimeInvestment,
            paymentPeriod,
            calculatePlannedInvestment,
            minPeriodicPaymentFor3B,
        ],
    );

    useEffect(() => {
        if (
            periodicPayment === 0
            && oneTimeInvestment < minOneTimeInvestmentFor3B
        ) {
            setOneTimeInvestment(minOneTimeInvestmentFor3B);
        }
    }, [minOneTimeInvestmentFor3B, periodicPayment, oneTimeInvestment]);

    const onOneTimePaymentChange = useCallback(
        (value) => {
            clearTimeout(transferValueTimeoutId);

            transferValueTimeoutId = setTimeout(() => {
                setOneTimeInvestment(value);
                setPlannedInvestment(
                    calculatePlannedInvestment(
                        value,
                        periodicPayment as number,
                        paymentPeriod as number,
                    ),
                );
            }, INPUT_TIMEOUT);
        },
        [periodicPayment, paymentPeriod, calculatePlannedInvestment],
    );

    const onPaymentPeriodChange = useCallback(
        (value) => {
            setPaymentPeriod(value);
            setPlannedInvestment(
                calculatePlannedInvestment(
                    oneTimeInvestment,
                    periodicPayment as number,
                    value,
                ),
            );
        },
        [oneTimeInvestment, periodicPayment, calculatePlannedInvestment],
    );

    const onProjectionYearsChange = useCallback((val) => {
        clearTimeout(projectionYearsTimeoutId);

        projectionYearsTimeoutId = setTimeout(() => {
            setProjectionYears(val);
        }, 500);
    }, []);

    const setProjectionYearsValue = useCallback((val) => {
        setProjectionYears(val);
    }, []);

    const isInvestmentAllowed = useMemo(() => {
        const plannedInvestmentForNext6Months = getInvestmentForNextSixMonths();

        return (
            plannedInvestmentForNext6Months
                <= MAX_INVESTMENT_FOR_NEXT_6_MONTHS
            || (plannedInvestmentForNext6Months
                > MAX_INVESTMENT_FOR_NEXT_6_MONTHS
                && shouldInitiateAdvisoryProcess)
        );
    }, [shouldInitiateAdvisoryProcess, getInvestmentForNextSixMonths]);

    const isProofOfAssetsRequired = useMemo(
        () => oneTimeInvestment > MAX_INVESTMENT_WITHOUT_PROOF_OF_ASSETS
            && selectedProductSelector.externalId === THREEBTYPES.zic,
        [oneTimeInvestment, selectedProductSelector?.externalId],
    );

    useEffect(() => {
        if (!plannedInvestment) {
            setPlannedInvestment(
                calculatePlannedInvestment(
                    oneTimeInvestment,
                    periodicPayment as number,
                    paymentPeriod as number,
                ),
            );
        }
    }, [
        oneTimeInvestment,
        paymentPeriod,
        periodicPayment,
        calculatePlannedInvestment,
        plannedInvestment,
    ]);

    const radioFullTransferOptions = useMemo(
        () => [
            {
                label: t(
                    'portfolio.createNewPortfolio.configuration.options.fullTransfer',
                ),
                value: 'true',
                disabled: false,
                error: false,
            },
            {
                label: t(
                    'portfolio.createNewPortfolio.configuration.options.partialTransfer',
                ),
                value: 'false',
                disabled: false,
                error: false,
            },
        ],
        [],
    );

    useEffect(() => {
        const selectedInvestmentHorizon = currentInvestmentApplicationSelector?.investmentDescription
            ?.selectStrategy?.investmentHorizon;

        if (isZivZifd && selectedInvestmentHorizon != null) {
            setProjectionYears(
                PROJECTION_YEARS_PER_INVESTMENT_HORIZON?.[
                    selectedInvestmentHorizon
                ],
            );
        } else if (currentClientSelector?.age && typeof currentClientSelector?.age === 'number') {
            setProjectionYears(65 - currentClientSelector.age);
        }
    }, [
        currentClientSelector?.age,
        isZivZifd,
        currentInvestmentApplicationSelector?.selectStrategy?.investmentHorizon,
    ]);

    const minTransferZurichEmployee = useMemo(
        () => (isZurichEmployee
            ? productSettings?.transferAmount?.transferAmountMinZurich
            : null),
        [
            isZurichEmployee,
            productSettings?.transferAmount?.transferAmountMinZurich,
        ],
    );

    const getMinTransferValue = useCallback(() => {
        if (is3aPremiumProductGroupSelector) {
            if (totalPillar3aAmountSelector >= 50000) {
                return 0;
            }
            if (minTransferZurichEmployee) {
                return minTransferZurichEmployee;
            }

            return productSettings?.transferAmount?.transferAmountMin;
        }
    }, [
        is3aPremiumProductGroupSelector,
        minTransferZurichEmployee,
        productSettings?.transferAmount?.transferAmountMin,
    ]);

    useEffect(() => {
        if (productSettings) {
            setMinMaxTransferValue({
                min: getMinTransferValue(),
                max: productSettings?.transferAmount?.transferAmountMax,
                minZurichEmployee: minTransferZurichEmployee,
            });

            const calculatedMinMaxYearlyPayment = {
                min: 0,
                max: affiliatedToPensionFund
                    ? productSettings?.maximumYearlyContributionWithPensionFund
                        ?.isDependantOnAffiliatedWithPensionFundTrue
                    : productSettings?.maximumYearlyContributionWithPensionFund
                        ?.isDependantOnAffiliatedWithPensionFundFalse,
            };

            setMinMaxYearlyPayment(calculatedMinMaxYearlyPayment);

            if (is3aProduct || is3aPremiumProductGroupSelector) {
                const defaultYearlyPayment = affiliatedToPensionFund
                    ? productSettings?.defaultYearlyContributionWithPensionFund
                        ?.isDependantOnAffiliatedWithPensionFundTrue
                    : productSettings?.defaultYearlyContributionWithPensionFund
                        ?.isDependantOnAffiliatedWithPensionFundFalse;

                let calculatedYearlyPayments = 0;

                if (
                    currentInvestmentDescriptionSelector?.productConfiguration
                        ?.yearlyPayments
                    && currentInvestmentDescriptionSelector?.productConfiguration
                        ?.yearlyPayments > calculatedMinMaxYearlyPayment?.max
                ) {
                    calculatedYearlyPayments = calculatedMinMaxYearlyPayment?.max;
                } else if (
                    currentInvestmentDescriptionSelector?.productConfiguration
                        ?.yearlyPayments !== null
                ) {
                    calculatedYearlyPayments = currentInvestmentDescriptionSelector
                        ?.productConfiguration?.yearlyPayments;
                } else if (
                    is3aProduct && currentInvestmentDescriptionSelector?.productConfiguration
                        ?.initialInvestment
                    && currentInvestmentDescriptionSelector?.productConfiguration
                        ?.initialInvestment > calculatedMinMaxYearlyPayment?.max
                ) {
                    calculatedYearlyPayments = calculatedMinMaxYearlyPayment?.max;
                } else if (
                    is3aProduct && currentInvestmentDescriptionSelector?.productConfiguration
                        ?.initialInvestment
                ) {
                    calculatedYearlyPayments = currentInvestmentDescriptionSelector
                        ?.productConfiguration?.initialInvestment;
                } else {
                    calculatedYearlyPayments = defaultYearlyPayment;
                }
                setYearlyPayment(calculatedYearlyPayments);
            }

            if (
                !productSettings?.showYearlyPayments
                || is3aPremiumProductGroupSelector
            ) {
                let calculatedTransferValue;
                const minimumTransferAmount = (is3aPremiumProductGroupSelector && totalPillar3aAmountSelector >= 50000) ? 0 : productSettings?.transferAmount?.transferAmountMin;

                if (
                    initialInvestmentAmount
                    && initialInvestmentAmount < minimumTransferAmount
                ) {
                    calculatedTransferValue = minimumTransferAmount;
                } else if (initialInvestmentAmount) {
                    calculatedTransferValue = initialInvestmentAmount;
                } else if (
                    currentInvestmentDescriptionSelector?.productConfiguration
                        ?.initialInvestment
                    && currentInvestmentDescriptionSelector?.productConfiguration
                        ?.initialInvestment < minimumTransferAmount
                ) {
                    calculatedTransferValue = minimumTransferAmount;
                } else if (
                    currentInvestmentDescriptionSelector?.productConfiguration
                        ?.initialInvestment
                    && currentInvestmentDescriptionSelector?.productConfiguration
                        ?.initialInvestment
                        > productSettings?.transferAmount?.transferAmountMax
                ) {
                    calculatedTransferValue = productSettings?.transferAmount?.transferAmountMax;
                } else {
                    calculatedTransferValue = currentInvestmentDescriptionSelector?.productConfiguration?.initialInvestment
                        || ((is3aPremiumProductGroupSelector && totalPillar3aAmountSelector >= 50000) ? 0 : productSettings?.defaultTransferAmount)
                        || 0;
                }
                changeTransferValue(calculatedTransferValue);
            }
        }
    }, [
        is3aProduct,
        is3aPremiumProductGroupSelector,
        productSettings,
        affiliatedToPensionFund,
        initialInvestmentAmount,
        currentInvestmentDescriptionSelector?.productConfiguration
            ?.yearlyPayments,
        minTransferZurichEmployee,
        getMinTransferValue,
        totalPillar3aAmountSelector,
    ]);

    useEffect(() => {
        if (isThreeB) {
            setRubrik(
                currentInvestmentDescriptionSelector?.productConfiguration
                    ?.rubrik,
            );
            setOneTimeInvestment(
                currentInvestmentDescriptionSelector?.productConfiguration
                    ?.initialInvestment
                    || productSettings?.defaultOneTimeInvestment,
            );
            setPeriodicPayment(
                currentInvestmentDescriptionSelector?.productConfiguration
                    ?.periodicalPaymentAmount
                    || productSettings?.defaultPeriodicPayment,
            );
            setPaymentPeriod(
                currentInvestmentDescriptionSelector?.productConfiguration
                    ?.periodicity || productSettings?.defaultPaymentFrequency,
            );
        }
    }, [currentInvestmentDescriptionSelector, isThreeB]);

    useEffect(() => {
        if (currentInvestmentApplicationSelector?.goals) {
            setTransferValue(initialInvestmentAmount);
        }
        if (
            showFullTransferQuestion
            && currentInvestmentApplicationSelector?.isFullTransfer !== null
            && currentInvestmentApplicationSelector?.isFullTransfer !== undefined
        ) {
            setFullTransfer(
                `${currentInvestmentApplicationSelector?.isFullTransfer}`,
            );
        }
        if (currentInvestmentApplicationSelector?.prevPensionScheme) {
            setPrevPensionScheme({
                ...currentInvestmentApplicationSelector?.prevPensionScheme,
            });
        } else if (currentInvestmentDescriptionSelector?.productConfiguration?.pensionScheme) {
            setPrevPensionScheme({
                nameOfPensionScheme: currentInvestmentDescriptionSelector?.productConfiguration?.pensionScheme,
                addressSupplement: currentInvestmentDescriptionSelector?.productConfiguration?.addressSuffix,
                postcodeAndCity: currentInvestmentDescriptionSelector?.productConfiguration?.zip,
                streetAndNumber: currentInvestmentDescriptionSelector?.productConfiguration?.street,
                policeNumber: currentInvestmentDescriptionSelector?.productConfiguration?.policyNo,
                bankAccountNumber: currentInvestmentDescriptionSelector?.productConfiguration?.bankAccountNo,
            });
        }
    }, [
        selectedProductSelector,
        currentInvestmentApplicationSelector?.goals,
        currentInvestmentApplicationSelector?.isFullTransfer,
        currentInvestmentApplicationSelector?.prevPensionScheme,
        initialInvestmentAmount,
        currentInvestmentDescriptionSelector,
    ]);

    const onFieldChange = useCallback(
        (field, validateCallback = validateInputString) => {
            const [fieldKey] = Object.keys(field);

            setPrevPensionScheme({
                ...prevPensionScheme,
                ...field,
            });
            setValidationPrevPensionScheme({
                ...validationPrevPensionScheme,
                ...{
                    [fieldKey]: validateCallback
                        ? validateCallback(field[fieldKey])
                        : null,
                },
            });
        },
        [
            validationPrevPensionScheme,
            setValidationPrevPensionScheme,
            setPrevPensionScheme,
            prevPensionScheme,
        ],
    );

    const yAxisFormat = useCallback(
        (num) => formatNumberRounding(num, getFormattedNumber),
        [getFormattedNumber],
    );

    const azpXAxisFormat = useCallback((context) => {
        if (!context) return '';
        const { isFirst, value } = context;

        if (isFirst) return `<b>${t('charts.years')}</b>`;
        const yearOffset = new Date(value).getFullYear() - new Date().getFullYear();

        return `${yearOffset >= 0 ? '+' : ''}${yearOffset}`;
    }, []);

    const azpYAxisFormat = useCallback((context) => {
        const { value } = context;

        return formatNumberRounding(value, getFormattedNumber);
    }, []);

    const tooltipFormat = useCallback(
        ({ point, series }) => ReactDOMServer.renderToString(
            ProjectionTooltip({
                point,
                series,
                currency,
                t,
                getFormattedCurrency,
            }),
        ),
        [currency, getFormattedCurrency, t],
    );

    const optional = useMemo(
        () => ({
            ...projectionOptional(t, currency, yAxisFormat, tooltipFormat),
            ...findMinAndMax([chartData]),
        }),
        [currency, t, yAxisFormat, chartData],
    );

    const onContinue = useCallback(async () => {
        try {
            const errors = errorModel({
                data: {
                    ...prevPensionScheme,
                    yearlyPayment,
                    transferValue,
                    rubrik,
                },
                showYearlyPayments: isThreeB
                    ? false
                    : productSettings?.showYearlyPayments,
                is3aPremiumProductGroupSelector,
                isThreeB,
                isAzp,
                isZic,
                isZifd,
            });

            if (!productSettings?.showYearlyPayments || transferValue > 0) {
                setValidationPrevPensionScheme(errors);
                if (IsAllDataNull(errors) === false) {
                    return;
                }
            }

            if (
                productSettings?.showYearlyPayments
                && !is3aPremiumProductGroupSelector
                && validateInputNumberWithZero(yearlyPayment) !== null
            ) {
                setValidationPrevPensionScheme(errors);

                return;
            }
            setIsContinueInProgress(true);

            let periodicity = null;

            if (isThreeB) {
                periodicity = periodicPayment === 0 ? null : parseInt(paymentPeriod, 10);
            }

            const payload = {
                initialInvestment: isThreeB
                    ? +oneTimeInvestment
                    : +parseFloat(transferValue).toFixed(2),
                periodicalPaymentAmount: isThreeB ? periodicPayment : null,
                periodicity,
                yearlyPayments: parseInt(yearlyPayment, 10),
                pensionScheme: isThreeB
                    ? null
                    : prevPensionScheme.nameOfPensionScheme,
                addressSuffix: prevPensionScheme.addressSupplement,
                street: prevPensionScheme.streetAndNumber,
                zip: prevPensionScheme.postcodeAndCity,
                policyNo: prevPensionScheme.policeNumber,
                bankAccountNo: prevPensionScheme.bankAccountNumber,
                rubrik: isThreeB ? rubrik : null,
            };

            if (showFullTransferQuestion) {
                payload.isFullTransfer = isFullTransfer === 'true';
            }

            const azpPayload = {
                initialInvestment: withdrawalAzpInvestmentAmount,
                yearlyPayments: 0,
                rubrik: isThreeB ? rubrik : null,
            };

            const result = await saveInvestmentApplicationDataAction({
                payload: isAzp ? azpPayload : payload,
                method: 'saveProductData',
                additionalData: {
                    goals: {
                        InstrumentAllocations: modelPortfolio.Positions.map(
                            ({ Allocation, Security: { Id } }) => ({
                                Allocation,
                                InstrumentId: Id,
                            }),
                        ),
                        InitialInvestmentAmount: isThreeB
                            ? oneTimeInvestment
                            : parseFloat(transferValue).toFixed(2),
                        YearlyContributionAmount: isThreeB
                            ? getYearlyContribution()
                            : parseInt(yearlyPayment, 10),
                        CurrencyId: modelPortfolio.BaseCurrency.Id,
                        PortfolioId: modelPortfolioId,
                        projectionYears:
                            projectionYears < 1 ? 0 : projectionYears,
                        plannedInvestment,
                    },
                    isFullTransfer: isFullTransfer === 'true',
                    prevPensionScheme,
                    chartOptions: optional,
                    currency: modelPortfolio?.BaseCurrency,
                },
            });

            if (!result) {
                setIsContinueInProgress(false);

                return;
            }

            stepComplete();
        } catch (err) {
            handlerRequestCanceling(
                HandlerError({
                    setError: setPageErrors,
                    setLoading,
                }),
            )(err);
            setIsContinueInProgress(false);
        }
    }, [
        modelPortfolio,
        transferValue,
        yearlyPayment,
        optional,
        isFullTransfer,
        showFullTransferQuestion,
        prevPensionScheme,
        isThreeB,
        oneTimeInvestment,
        periodicPayment,
        paymentPeriod,
        isWithdrawal,
        productSettings?.showYearlyPayments,
        rubrik,
        getYearlyContribution,
        isAzp,
        withdrawalAzpInvestmentAmount,
        plannedInvestment,
        is3aProduct,
        is3aPremiumProductGroupSelector,
    ]);

    const changeTransferValue = useCallback(
        (value) => {
            clearTimeout(transferValueTimeoutId);
            if (!productSettings?.showYearlyPayments) {
                setValidationPrevPensionScheme({
                    ...validationPrevPensionScheme,
                    ...{ transferValue: validateInputNumberWithZero(+value) },
                });
            }
            transferValueTimeoutId = setTimeout(() => {
                setTransferValue(+value);
            }, INPUT_TIMEOUT);
        },
        [validationPrevPensionScheme, productSettings?.showYearlyPayments],
    );

    const changeYearlyPayment = useCallback(
        (value) => {
            clearTimeout(yearlyPaymentTimeoutId);
            setValidationPrevPensionScheme({
                ...validationPrevPensionScheme,
                ...{
                    yearlyPayment: is3aPremiumProductGroupSelector
                        ? null
                        : validateInputNumberWithZero(+value),
                },
            });
            yearlyPaymentTimeoutId = setTimeout(() => {
                setYearlyPayment(+value);
            }, INPUT_TIMEOUT);
        },
        [validationPrevPensionScheme, is3aPremiumProductGroupSelector],
    );

    const changeFullTransfer = useCallback(
        (e) => {
            setFullTransfer(e?.target?.value);
        },
        [isFullTransfer],
    );

    const getSelectStrategy = () => currentInvestmentApplicationSelector?.riskScore;

    useEffect(() => {
        if (
            !isZic
            && modelPortfolio
            && ((transferValue && !productSettings?.showYearlyPayments)
                || (productSettings?.showYearlyPayments
                    && (yearlyPayment || transferValue))
                || isThreeB)
        ) {
            (async () => {
                const performance: any = {
                    language,
                    InstrumentAllocations: modelPortfolio?.Positions?.map(
                        ({ Allocation, Security: { Id } }) => ({
                            Allocation,
                            InstrumentId: Id,
                        }),
                    ),
                    InitialInvestmentAmount: isThreeB
                        ? +oneTimeInvestment
                        : +parseFloat(String(transferValue)).toFixed(2),
                    YearlyContributionAmount: isThreeB
                        ? getYearlyContribution()
                        : parseInt(String(yearlyPayment), 10),
                    CurrencyId: modelPortfolio?.BaseCurrency?.Id,
                    PortfolioId: modelPortfolioId,
                    projectionYears: projectionYears < 1 ? 0 : projectionYears,
                    individualPortfolio,
                    investmentApplicationId: currentInvestmentApplicationId,
                };

                // Investment projection is not needed if the initial investment amount and yearly contribution amount are both 0
                if (
                    performance.InitialInvestmentAmount === 0
                    && performance.YearlyContributionAmount === 0
                ) {
                    return;
                }

                getInvestmentProjection(performance).then((response) => {
                    setLoading(true);
                    const chartGraphData = response.data?.projections;
                    const AdditionalValueArray1 = chartGraphData[1]?.values.map(
                        (k, i) => ({
                            ...k,
                            AdditionalValue:
                                chartGraphData[0]?.values[i]?.value,
                            value: k?.value,
                            date: k?.date,
                        }),
                    );
                    const AdditionalValueArray2 = chartGraphData[2]?.values.map(
                        (k, i) => ({
                            ...k,
                            AdditionalValue:
                                chartGraphData[1]?.values[i]?.value,
                            value: k?.value,
                            date: k?.date,
                        }),
                    );

                    const chart = newAdaptProjectionData([
                        {
                            Values: [...AdditionalValueArray1],
                            ProbabilityPercentage:
                                chartGraphData[1]?.stdDevFactor,
                            SeriesType: 'InstrumentSet',
                        },
                        {
                            Values: [...AdditionalValueArray1],
                            ProbabilityPercentage:
                                chartGraphData[1]?.stdDevFactor,
                            SeriesType: 'InstrumentSet',
                        },
                        {
                            Values: [...AdditionalValueArray2],
                            ProbabilityPercentage:
                                chartGraphData[2]?.stdDevFactor,
                            SeriesType: 'InstrumentSet',
                        },
                    ]);

                    const { bestReturn, averageReturn, worstReturn } = response?.data || {};

                    setReturnRates([
                        {
                            name: t('projectionLegend.bestCase'),
                            value: `${bestReturn > 0 ? '+' : '-'} ${Math.abs(
                                bestReturn * 100,
                            )?.toFixed(2)}%`,
                        },
                        {
                            name: t('projectionLegend.averageCase'),
                            value: `${averageReturn > 0 ? '+' : '-'} ${Math.abs(
                                averageReturn * 100,
                            )?.toFixed(2)}%`,
                        },
                        {
                            name: t('projectionLegend.worstCase'),
                            value: `${worstReturn > 0 ? '+' : '-'} ${Math.abs(
                                worstReturn * 100,
                            )?.toFixed(2)}%`,
                        },
                    ]);

                    setChartData(chart);
                    setLoading(false);
                });
            })();
        }
    }, [
        isZic,
        modelPortfolioId,
        transferValue,
        yearlyPayment,
        modelPortfolio,
        language,
        productSettings?.showYearlyPayments,
        individualPortfolio,
        oneTimeInvestment,
        isThreeB,
        projectionYears,
        getYearlyContribution,
        currentInvestmentApplicationId,
    ]);

    const { stepComplete } = usePortfolioProcessNavigation({
        saveOnContinue: onContinue,
        enableAutoStepComplete: false,
        continueDisabled: isContinueInProgress || continueDisabledFromProps,
        navigationDisabled: isLoading || isModelPortfolioLoading,
        pageErrors,
        continueInProgress: isContinueInProgress,
    });

    return {
        isPaymentPeriodVisible,
        projectionOptions: optional,
        currency,
        riskCategories: riskCategoriesList,
        t,
        showFullTransferQuestion,
        modelPortfolio,
        productSettings,
        productName,
        isFullTransfer,
        radioFullTransferOptions,
        changeFullTransfer,
        isLoading,
        chartData,
        returnRates,
        transferValue,
        yearlyPayment,
        changeTransferValue,
        changeYearlyPayment,
        minMaxTransferValue,
        minMaxYearlyPayment,
        validation: validationPrevPensionScheme,
        prevPensionScheme,
        onFieldChange,
        getSelectStrategy,
        is3aProduct,
        isThreeB,
        isVbVbPremiumProduct,
        periodicPayment,
        oneTimeInvestment,
        onPeriodicPaymentChange,
        onOneTimePaymentChange,
        onPaymentPeriodChange,
        paymentPeriod,
        plannedInvestment,
        rubrik,
        setRubrik,
        projectionYears,
        onProjectionYearsChange,
        getInvestmentForNextSixMonths,
        isInvestmentAllowed,
        isProofOfAssetsRequired,
        azpXAxisFormat,
        azpYAxisFormat,
        setProjectionYearsValue,
        language,
        selectedProductDescSelector,
        selectedProductSelector,
        currentInvestmentDescriptionSelector,
        isZurichEmployee,
        minPeriodicPaymentFor3B,
        minOneTimeInvestmentFor3B,
        isAzp,
        isZic,
        isZifd,
        is3aPremiumProductGroupSelector,
    };
};
